import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Stale-while-revalidate cache ──────────────────────────────────
//
// Frontend perf win: many pages (Masters dropdowns, SKU view, dispatch
// picker) re-fetch identical GETs on every navigation because nothing
// dedupes between unrelated React components. This in-memory cache:
//   1. Serves a fresh-enough cached body INSTANTLY (no network), so
//      navigation feels click-instant.
//   2. Still revalidates in the background so the user eventually sees
//      latest data — without ever blocking the UI on a network call.
//
// Endpoints are matched against the SWR_RULES table. Anything not in
// the table goes straight through (default behavior preserved). TTLs
// reflect "how stale would a user notice / care": buyer master rarely
// changes, finished stock changes minutes apart, etc.
//
// Cache keys include the full querystring so different filters get
// their own slot. POST/PUT/DELETE invalidate prefix-matching cached
// entries automatically.
const SWR_RULES = [
  // Static-ish masters — refresh every 5 min.
  // NOTE: `/styles/manufacturing-costs` and `/finished-stock/batch-costs`
  // are RBAC-dependent (viewer list can change) and depend on live
  // overhead compute — we skip them explicitly so they bypass the SWR
  // cache entirely.
  { re: /^\/styles\/manufacturing-costs/, ttl: 0 },
  { re: /^\/finished-stock\/batch-costs/, ttl: 0 },
  { re: /^\/overheads\/compute$/, ttl: 0 },
  { re: /^\/(buyers|vendors|styles|articles|designs|sku_catalog)(\?|$)/, ttl: 300_000 },
  { re: /^\/auth\/users(\?|$)/, ttl: 300_000 },
  // Finished stock + groups — refresh every 30s
  { re: /^\/finished-stock\/(skus|groups)(\?|$)/, ttl: 30_000 },
  // Dispatch / pipeline lists — refresh every 20s
  { re: /^\/dispatches\/v2(\?|$)/, ttl: 20_000 },
  { re: /^\/cutting-orders(\?|$)/, ttl: 20_000 },
  { re: /^\/job-work(\?|$)/, ttl: 20_000 },
  { re: /^\/job-work\/dashboard$/, ttl: 20_000 },
];
const _cache = new Map();  // key → { body, exp, inflight }

function _swrRule(url) {
  const path = url.startsWith("http") ? new URL(url).pathname.replace(/^\/api/, "") : url;
  for (const r of SWR_RULES) {
    if (r.re.test(path)) {
      // ttl=0 means "no cache". Return null so the caller skips SWR.
      return r.ttl > 0 ? r : null;
    }
  }
  return null;
}

function _cacheKey(method, url, params) {
  return `${method.toUpperCase()} ${url}${params ? "?" + JSON.stringify(params) : ""}`;
}

function _invalidate(url) {
  // On a mutation, drop every cached entry whose URL starts with the
  // mutated resource's base path (best-effort; cheap & predictable).
  const base = (url || "").split("?")[0].replace(/\/[^/]+$/, "");
  for (const key of _cache.keys()) {
    if (key.includes(base)) _cache.delete(key);
  }
}

// Wrap api.get to add SWR. Other axios verbs invalidate the cache.
const _rawGet = api.get.bind(api);
api.get = (url, config) => {
  const rule = _swrRule(url);
  if (!rule) return _rawGet(url, config);
  const key = _cacheKey("GET", url, config?.params);
  const now = Date.now();
  const entry = _cache.get(key);
  // Cache hit — return cached body immediately; revalidate in background.
  if (entry && entry.body) {
    if (entry.exp > now) {
      // Fresh — no revalidate needed.
      return Promise.resolve(entry.body);
    }
    // Stale — kick off background refresh but DON'T await it
    if (!entry.inflight) {
      entry.inflight = _rawGet(url, config).then((res) => {
        entry.body = res;
        entry.exp = Date.now() + rule.ttl;
        entry.inflight = null;
        return res;
      }).catch(() => { entry.inflight = null; });
    }
    return Promise.resolve(entry.body);
  }
  // No cache yet — actually wait for the network response
  const p = _rawGet(url, config).then((res) => {
    _cache.set(key, { body: res, exp: Date.now() + rule.ttl, inflight: null });
    return res;
  });
  return p;
};
for (const verb of ["post", "put", "patch", "delete"]) {
  const raw = api[verb].bind(api);
  api[verb] = (url, ...rest) => {
    _invalidate(url);
    return raw(url, ...rest);
  };
}
export function clearApiCache() { _cache.clear(); }

// Global hook for the SessionExpiredModal. When set, a 401 from any
// authenticated request fires this callback instead of dropping the user
// to /login — the in-flight form data stays in the DOM and the user
// re-enters their password in an overlay modal.
let _sessionExpiredHandler = null;
export function setSessionExpiredHandler(fn) {
  _sessionExpiredHandler = fn;
}

api.interceptors.response.use(
  (r) => r,
  (e) => {
    const status = e?.response?.status;
    const url = e?.config?.url || "";
    // 401: token expired / invalid. ProtectedRoute handles /auth/me itself
    // (initial load with no session); for every other call we want the
    // in-place re-login modal so the user doesn't lose their work.
    if (status === 401 && !url.includes("/auth/me") && _sessionExpiredHandler) {
      _sessionExpiredHandler(e);
    }
    // Surface 403s with a friendly toast so users immediately know the
    // action is not permitted.
    if (status === 403 && !url.includes("/auth/me")) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : "User Access Not Granted"
      );
    }
    return Promise.reject(e);
  }
);

export default api;

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return JSON.stringify(d);
}
